import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Button from 'src/components/button';
import {
  ageValidator,
  underFifteenError,
  underTwentyOneError,
} from 'src/components/inputs/formik-credentials-input/formik-credentials-input';
import CommunityCollegeStudent from 'shared/img/cc-student.svg';
import HighSchoolStudent from 'shared/img/hs-student.svg';
import {Form, Formik, FormikHelpers} from 'formik';
import FormikTextInput from '../../../../components/inputs/formik-text-input';
import Label from '../../../../../shared/components/label';
import errorTypes from '../../../../components/form/onboarding/text.json';
import usePlatformContext from '../../../../hooks/usePlatform';
import {EducationPhase, RegistrationSteps} from 'src/types/enums';
import placeholders from 'src/static/placeholders.json';
import {setNavigationAnnouncement} from '../../../../../shared/actions/accessibility-announcement';
import Errors from '../../../../static/errors.json';
import {StepPosition} from '../../../../types/form';
import {useDispatch} from 'react-redux';
import {BirthdayForm} from '../../../../types/onboarding';
import AppLayout from 'src/components/app-layout';
import AuthLayout from 'src/views/public/shared/layout';
import {deleteStudent} from '../../../../redux/student/user';
import Yup from '../../../../utils/yup';
import {TOO_YOUNG} from 'src/views/public/utils';
import TextButton from '../../../../components/TextButton';

const createDobAndNameSignupFormSchema = (
  ageValidationType: 'underTwentyOneError' | 'underFifteenError'
) => {
  const ageValidationError =
    ageValidationType === 'underTwentyOneError' ? underTwentyOneError : underFifteenError;

  return Yup.object().shape({
    firstName: Yup.string().required(Errors.validation.firstName.required),
    lastName: Yup.string().required(Errors.validation.lastName.required),
    birthday: Yup.date()
      .required(Errors.validation.birthday.required)
      .test(ageValidationType, ageValidationError, function (value) {
        return value instanceof Date && ageValidator(value, ageValidationType);
      }),
  });
};

const USER_MAP = {
  [EducationPhase.HIGH_SCHOOL]: {
    icon: HighSchoolStudent,
    text: 'High School Student',
  },
  [EducationPhase.COMMUNITY_COLLEGE]: {
    icon: CommunityCollegeStudent,
    text: 'College Transfer Student',
  },
};

interface Props<Values> {
  size: number;
  student: Values;
  updateUser: (
    values: Values,
    stepPosition: StepPosition,
    form: FormikHelpers<Values>
  ) => Promise<boolean>;
}

const usertypeData = (userType: string) => USER_MAP[userType];

function NameAndDobForm<Values extends BirthdayForm>({size, student, updateUser}: Props<Values>) {
  const {isIonic} = usePlatformContext();
  const [accountDeleted, setAccountDeleted] = useState(false);
  const stepPosition: StepPosition = {key: RegistrationSteps.NAME_AND_DOB, index: 0};
  const dispatch = useDispatch();

  const onSubmit = async (values, formik) => {
    const result = await updateUser(values, stepPosition, formik);

    if (result !== true && Array.isArray(result)) {
      const hasBirthdayTooYoungError = result.some(
        (error) => error.type === 'BirthdayTooYoungError'
      );

      if (hasBirthdayTooYoungError) {
        window.sessionStorage.setItem(TOO_YOUNG, 'true');
        setAccountDeleted(true);
        await dispatch(deleteStudent(student._id, 'automatic deletion - too young', false));
      }
    }

    return null;
  };

  useEffect(() => {
    dispatch(setNavigationAnnouncement(`${usertypeData(student.educationPhase).text} Sign Up`));
  }, [dispatch, student.educationPhase]);

  const {icon: Icon} = usertypeData(student.educationPhase);

  const deleteStudentAndRedirect = async () => {
    await dispatch(
      deleteStudent(
        student._id,
        `automatic deletion - ${isHighSchoolStudent ? 'too old' : 'too young'}`,
        isIonic ? '/welcome' : '/signup'
      )
    );
  };

  const backToWelcomeButton = (
    <div className="back-to-welcome-button">
      <TextButton onClick={deleteStudentAndRedirect}>
        Did you mean to sign up as a different type of user?
      </TextButton>
    </div>
  );

  const showBackToWelcomeButton = (error) =>
    [
      errorTypes.birthdayErrors.overage,
      errorTypes.birthdayErrors.underageCommunityCollege,
    ].includes(error)
      ? backToWelcomeButton
      : '';

  const isHighSchoolStudent = student.educationPhase === EducationPhase.HIGH_SCHOOL;

  return (
    <AppLayout topToolbars={null}>
      <AuthLayout page="signup">
        <div className="auth-signup auth-shared-credentials-container">
          <div className="auth-signup-user-icon">
            <Icon width={size} height={size} />
          </div>
          <h1 className="auth-signup-user-header">Welcome to Raise Me!</h1>
          <p className="auth-subheader">Tell us a bit more about yourself</p>

          {!accountDeleted ? (
            <Formik
              initialValues={{
                firstName: '',
                lastName: '',
                birthday: '',
              }}
              onSubmit={async (values, formik) => {
                const formData = {
                  firstName: values.firstName,
                  lastName: values.lastName,
                  birthday: new Date(values.birthday),
                };

                const errors = await onSubmit(formData, formik);
                if (errors) {
                  Object.keys(errors).forEach((field) => {
                    if (errors[field]) {
                      formik.setFieldError(field, errors[field]);
                    }
                  });
                }
              }}
              validationSchema={
                isHighSchoolStudent
                  ? createDobAndNameSignupFormSchema('underTwentyOneError')
                  : createDobAndNameSignupFormSchema('underFifteenError')
              }
              validateOnBlur
              validateOnChange={false}
            >
              {(formikProps) => (
                <Form>
                  <legend className="shared-required-legend"></legend>
                  <Label required>
                    First Name
                    <FormikTextInput
                      name="firstName"
                      showCheck
                      placeholder={placeholders.signUpFirstName}
                      autoComplete="given-name"
                      required
                    />
                  </Label>
                  <Label required>
                    Last Name
                    <FormikTextInput
                      name="lastName"
                      showCheck
                      placeholder={placeholders.signUpLastName}
                      autoComplete="family-name"
                      required
                    />
                  </Label>
                  <Label required>
                    Date of Birth
                    <FormikTextInput name="birthday" type="date" required />
                  </Label>
                  {showBackToWelcomeButton(formikProps.errors.birthday)}
                  <p className="auth-signup-disclaimer">
                    By agreeing and continuing, you acknowledge and agree to the{' '}
                    <Link to="/terms-of-use" className="auth-shared-link caption">
                      Terms of Use
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy-policy" className="auth-shared-link caption">
                      Privacy and Cookie Policy
                    </Link>
                  </p>
                  <Button
                    size="large"
                    type="submit"
                    className="auth-shared-submit-button"
                    cy="signup"
                    width="100%"
                    disabled={formikProps.isSubmitting || !formikProps.isValid}
                  >
                    {formikProps.isSubmitting ? 'Signing Up...' : 'Agree & Continue'}
                  </Button>
                </Form>
              )}
            </Formik>
          ) : (
            <p className="error">
              Sorry, we can&apos;t complete your account creation at this time and your account has
              been deleted. You can close out of this screen.
            </p>
          )}
        </div>
      </AuthLayout>
    </AppLayout>
  );
}

export default NameAndDobForm;
